import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { flowMachine, flowReducer } from './flowMachine'
import { VerificationFlowContextType, VerificationFlowOptions, VerificationFlowState, PartnerType } from './types'
import { TopicKey } from '../../../constants/customerMessages'
import { selectCustomerIsLoanOfficer } from '../../../store/selectors/customerProfile'

// Default options
const defaultOptions: VerificationFlowOptions = {
  enableVerificationFlow: false,
  initialPartnerType: 'RealEstateAgent',
  realEstateAgentMembership: null,
  loanOfficerMembership: null,
  includePrequal: false,
  prequalOnly: false
}

// Create context
const VerificationFlowContext = createContext<VerificationFlowContextType | undefined>(undefined)

interface VerificationFlowProviderProps {
  children: React.ReactNode
  options?: Partial<VerificationFlowOptions>
}

export const VerificationFlowProvider = ({ children, options: userOptions = {} }: VerificationFlowProviderProps) => {
  const options = { ...defaultOptions, ...userOptions }

  // Determine customer profile data internally using Redux selectors
  const customerIsLoanOfficer = useSelector(selectCustomerIsLoanOfficer)

  // Initialize state
  const initialState: VerificationFlowState = {
    currentStep: flowMachine.initial,
    partnerType: options.initialPartnerType,
    currentPartnerType: options.initialPartnerType,
    realEstateAgentMembership: options.realEstateAgentMembership,
    loanOfficerMembership: options.loanOfficerMembership,
    insuranceAgentMembership: options.insuranceAgentMembership,
    isVerified: options.realEstateAgentMembership?.isVerified || false,
    includePrequal: options.includePrequal || false,
    prequalOnly: options.prequalOnly || false,
    customerIsLoanOfficer: customerIsLoanOfficer,
    onComplete: options.onComplete || (() => {}),
    onSkip: options.onSkip || (() => {})
  }

  const [state, dispatch] = useReducer(flowReducer, initialState)

  // Update customer profile data when it changes
  useEffect(() => {
    if (customerIsLoanOfficer !== state.customerIsLoanOfficer) {
      dispatch({
        type: 'UPDATE_CUSTOMER_PROFILE',
        payload: {
          customerIsLoanOfficer
        }
      })
    }
  }, [customerIsLoanOfficer, state.customerIsLoanOfficer])

  // Update memberships when they change after initialization
  useEffect(() => {
    if (options.realEstateAgentMembership !== state.realEstateAgentMembership) {
      dispatch({
        type: 'UPDATE_MEMBERSHIP',
        payload: {
          membership: options.realEstateAgentMembership,
          partnerType: 'RealEstateAgent'
        }
      })
    }
  }, [options.realEstateAgentMembership, state.realEstateAgentMembership])

  useEffect(() => {
    if (options.loanOfficerMembership !== state.loanOfficerMembership) {
      dispatch({
        type: 'UPDATE_MEMBERSHIP',
        payload: {
          membership: options.loanOfficerMembership,
          partnerType: 'LoanOfficer'
        }
      })
    }
  }, [options.loanOfficerMembership, state.loanOfficerMembership])

  useEffect(() => {
    if (options.insuranceAgentMembership !== state.insuranceAgentMembership) {
      dispatch({
        type: 'UPDATE_MEMBERSHIP',
        payload: {
          membership: options.insuranceAgentMembership,
          partnerType: 'InsuranceAgent'
        }
      })
    }
  }, [options.insuranceAgentMembership, state.insuranceAgentMembership])

  // Helper function to start verification with a specific partner type
  const startVerification = (
    message: string,
    verifiedTopicKey: TopicKey,
    defaultTopicKey: TopicKey,
    partnerType?: PartnerType,
    metadata?: Record<string, any>
  ) => {
    const reaMembership = options.realEstateAgentMembership
    const loMembership = options.loanOfficerMembership
    const insuranceMembership = options.insuranceAgentMembership
    const targetPartnerType = partnerType || options.initialPartnerType

    // If verification flow is disabled or there's no membership to verify,
    // skip verification and send the message directly with the default topic key.
    // The default topic key is what mikasa uses to control which customer
    // is should receive the message.
    if (
      !options.enableVerificationFlow ||
      (targetPartnerType === 'RealEstateAgent' && !reaMembership) ||
      (targetPartnerType === 'LoanOfficer' && !loMembership && !options.prequalOnly) ||
      (targetPartnerType === 'InsuranceAgent' && !insuranceMembership) ||
      (!reaMembership && !loMembership && !insuranceMembership)
    ) {
      dispatch({
        type: 'SEND_MESSAGE',
        payload: {
          message,
          topicKey: defaultTopicKey,
          partnerType: targetPartnerType,
          metadata
        }
      })
      return
    }

    // If loan officer membership is already verified, send message directly with verified topic key
    if (targetPartnerType === 'LoanOfficer' && loMembership?.isVerified) {
      dispatch({
        type: 'SEND_MESSAGE',
        payload: {
          message,
          topicKey: verifiedTopicKey,
          partnerType: targetPartnerType,
          metadata
        }
      })
      return
    }

    // If all else fails and verification is needed, start the verification flow
    dispatch({
      type: 'START_VERIFICATION',
      payload: {
        message,
        topicKey: verifiedTopicKey,
        partnerType: targetPartnerType,
        metadata
      }
    })
  }

  return (
    <VerificationFlowContext.Provider
      value={{
        state,
        dispatch,
        startVerification
      }}
    >
      {children}
    </VerificationFlowContext.Provider>
  )
}

// Custom hook to use the verification flow context
export const useVerificationFlow = (): VerificationFlowContextType => {
  const context = useContext(VerificationFlowContext)

  if (context === undefined) {
    throw new Error('useVerificationFlow must be used within a VerificationFlowProvider')
  }

  return context
}
